/**
 * SLP (Student Learning Portfolio) Service for MyClass Application
 * Handles all SLP-related operations including records, assessments, and learning objectives
 */

import { supabaseAdmin } from '../supabase.js';

class SLPService {
  constructor(tenantId) {
    this.tenantId = tenantId;
    this.actualTenantId = 'eadcd263-a399-4261-8cbd-8fb1ce6da0e3';
  }

  /**
   * Get SLP records for a teacher
   * @param {string} teacherId - Teacher ID
   * @param {Object} options - Query options
   * @returns {Object} SLP records data
   */
  async getTeacherSLP(teacherId, options = {}) {
    try {
      const { limit = 100, subject = null, classId = null, achievementLevel = null } = options;
      
      let query = supabaseAdmin
        .from('slp_records')
        .select(`
          *,
          student:saas_students(student_id, name, class_id),
          class:classes(class_code, class_name, form_level)
        `)
        .eq('teacher_id', teacherId)
        .eq('tenant_id', this.actualTenantId)
        .order('assessment_date', { ascending: false })
        .limit(limit);

      if (subject) {
        query = query.eq('subject', subject);
      }

      if (classId) {
        query = query.eq('class_id', classId);
      }

      if (achievementLevel) {
        query = query.eq('achievement_level', achievementLevel);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching teacher SLP:', error);
        return { data: null, error };
      }

      // Process SLP data with statistics
      const processedSLP = data?.map(record => {
        const achievementLevel = parseInt(record.achievement_level) || 0;
        const isCompleted = achievementLevel >= 4; // Assuming 4+ is completed
        
        return {
          ...record,
          is_completed: isCompleted,
          achievement_description: this.getAchievementDescription(achievementLevel)
        };
      }) || [];

      // Calculate summary statistics
      const summary = this.calculateSLPSummary(processedSLP);

      return {
        data: {
          slpRecords: processedSLP,
          summary
        },
        error: null
      };

    } catch (error) {
      console.error('Error in getTeacherSLP:', error);
      return { data: null, error: error.message };
    }
  }

  /**
   * Get SLP records for a specific class
   * @param {string} classId - Class ID
   * @param {Object} options - Query options
   * @returns {Object} Class SLP data
   */
  async getClassSLP(classId, options = {}) {
    try {
      const { limit = 100, subject = null, achievementLevel = null } = options;
      
      let query = supabaseAdmin
        .from('slp_records')
        .select(`
          *,
          student:saas_students(student_id, name),
          teacher:saas_teachers(teacher_id, name, subjectSpecialty)
        `)
        .eq('class_id', classId)
        .eq('tenant_id', this.actualTenantId)
        .order('assessment_date', { ascending: false })
        .limit(limit);

      if (subject) {
        query = query.eq('subject', subject);
      }

      if (achievementLevel) {
        query = query.eq('achievement_level', achievementLevel);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching class SLP:', error);
        return { data: null, error };
      }

      return { data: data || [], error: null };

    } catch (error) {
      console.error('Error in getClassSLP:', error);
      return { data: null, error: error.message };
    }
  }

  /**
   * Get student SLP records
   * @param {string} studentId - Student ID
   * @param {Object} options - Query options
   * @returns {Object} Student SLP data
   */
  async getStudentSLP(studentId, options = {}) {
    try {
      const { limit = 100, subject = null, classId = null } = options;
      
      let query = supabaseAdmin
        .from('slp_records')
        .select(`
          *,
          teacher:saas_teachers(teacher_id, name, subjectSpecialty),
          class:classes(class_code, class_name, form_level)
        `)
        .eq('student_id', studentId)
        .eq('tenant_id', this.actualTenantId)
        .order('assessment_date', { ascending: false })
        .limit(limit);

      if (subject) {
        query = query.eq('subject', subject);
      }

      if (classId) {
        query = query.eq('class_id', classId);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching student SLP:', error);
        return { data: null, error };
      }

      // Process SLP data and calculate statistics
      const processedSLP = data?.map(record => {
        const achievementLevel = parseInt(record.achievement_level) || 0;
        const isCompleted = achievementLevel >= 4;
        
        return {
          ...record,
          is_completed: isCompleted,
          achievement_description: this.getAchievementDescription(achievementLevel)
        };
      }) || [];

      const summary = this.calculateSLPSummary(processedSLP);

      return {
        data: {
          slpRecords: processedSLP,
          summary
        },
        error: null
      };

    } catch (error) {
      console.error('Error in getStudentSLP:', error);
      return { data: null, error: error.message };
    }
  }

  /**
   * Create SLP record
   * @param {Object} slpData - SLP record data
   * @returns {Object} Created SLP record
   */
  async createSLPRecord(slpData) {
    try {
      const achievementLevel = parseInt(slpData.achievement_level) || 0;
      const isCompleted = achievementLevel >= 4;
      
      const { data, error } = await supabaseAdmin
        .from('slp_records')
        .insert([{
          ...slpData,
          is_completed: isCompleted,
          tenant_id: this.actualTenantId,
          created_at: new Date().toISOString()
        }])
        .select()
        .single();

      if (error) {
        console.error('Error creating SLP record:', error);
        return { data: null, error };
      }

      return { data, error: null };

    } catch (error) {
      console.error('Error in createSLPRecord:', error);
      return { data: null, error: error.message };
    }
  }

  /**
   * Update SLP record
   * @param {string} recordId - Record ID
   * @param {Object} updateData - Update data
   * @returns {Object} Updated SLP record
   */
  async updateSLPRecord(recordId, updateData) {
    try {
      const achievementLevel = parseInt(updateData.achievement_level) || parseInt(updateData.achievement_level);
      const isCompleted = achievementLevel >= 4;
      
      const { data, error } = await supabaseAdmin
        .from('slp_records')
        .update({
          ...updateData,
          is_completed: isCompleted,
          updated_at: new Date().toISOString()
        })
        .eq('id', recordId)
        .eq('tenant_id', this.actualTenantId)
        .select()
        .single();

      if (error) {
        console.error('Error updating SLP record:', error);
        return { data: null, error };
      }

      return { data, error: null };

    } catch (error) {
      console.error('Error in updateSLPRecord:', error);
      return { data: null, error: error.message };
    }
  }

  /**
   * Delete SLP record
   * @param {string} recordId - Record ID
   * @returns {Object} Deletion result
   */
  async deleteSLPRecord(recordId) {
    try {
      const { error } = await supabaseAdmin
        .from('slp_records')
        .delete()
        .eq('id', recordId)
        .eq('tenant_id', this.actualTenantId);

      if (error) {
        console.error('Error deleting SLP record:', error);
        return { data: null, error };
      }

      return { data: { success: true }, error: null };

    } catch (error) {
      console.error('Error in deleteSLPRecord:', error);
      return { data: null, error: error.message };
    }
  }

  /**
   * Get SLP statistics for a teacher
   * @param {string} teacherId - Teacher ID
   * @returns {Object} SLP statistics
   */
  async getSLPStatistics(teacherId) {
    try {
      const { data, error } = await supabaseAdmin
        .from('slp_records')
        .select('*')
        .eq('teacher_id', teacherId)
        .eq('tenant_id', this.actualTenantId);

      if (error) {
        console.error('Error fetching SLP statistics:', error);
        return { data: null, error };
      }

      const records = data || [];
      const summary = this.calculateSLPSummary(records);

      // Group by subject
      const subjectStats = {};
      records.forEach(record => {
        if (!subjectStats[record.subject]) {
          subjectStats[record.subject] = {
            total: 0,
            completed: 0,
            averageLevel: 0,
            levels: []
          };
        }
        subjectStats[record.subject].total++;
        const level = parseInt(record.achievement_level) || 0;
        subjectStats[record.subject].levels.push(level);
        if (level >= 4) {
          subjectStats[record.subject].completed++;
        }
      });

      // Calculate statistics for each subject
      Object.keys(subjectStats).forEach(subject => {
        const stats = subjectStats[subject];
        const validLevels = stats.levels.filter(l => !isNaN(l));
        stats.averageLevel = validLevels.length > 0 ? validLevels.reduce((sum, l) => sum + l, 0) / validLevels.length : 0;
        stats.completionRate = stats.total > 0 ? (stats.completed / stats.total) * 100 : 0;
      });

      return {
        data: {
          ...summary,
          subjectStats
        },
        error: null
      };

    } catch (error) {
      console.error('Error in getSLPStatistics:', error);
      return { data: null, error: error.message };
    }
  }

  /**
   * Get achievement level description
   * @param {number} level - Achievement level
   * @returns {string} Achievement description
   */
  getAchievementDescription(level) {
    const descriptions = {
      1: 'Beginning - 初級',
      2: 'Developing - 發展中',
      3: 'Proficient - 熟練',
      4: 'Advanced - 高級',
      5: 'Exemplary - 典範'
    };
    return descriptions[level] || 'Unknown';
  }

  /**
   * Calculate SLP summary statistics
   * @param {Array} records - Array of SLP records
   * @returns {Object} Summary statistics
   */
  calculateSLPSummary(records) {
    const validLevels = records.map(r => parseInt(r.achievement_level) || 0).filter(l => !isNaN(l));
    
    if (validLevels.length === 0) {
      return {
        total: 0,
        completed: 0,
        averageLevel: 0,
        completionRate: 0,
        subjects: [],
        classes: []
      };
    }

    const completed = validLevels.filter(l => l >= 4).length;

    return {
      total: validLevels.length,
      completed: completed,
      averageLevel: validLevels.reduce((sum, l) => sum + l, 0) / validLevels.length,
      completionRate: (completed / validLevels.length) * 100,
      subjects: [...new Set(records.map(r => r.subject))],
      classes: [...new Set(records.map(r => r.class_id))]
    };
  }

  /**
   * Get SLP progress for a student
   * @param {string} studentId - Student ID
   * @param {string} subject - Subject (optional)
   * @returns {Object} SLP progress data
   */
  async getStudentSLPProgress(studentId, subject = null) {
    try {
      let query = supabaseAdmin
        .from('slp_records')
        .select('achievement_level, subject, assessment_date, learning_objective')
        .eq('student_id', studentId)
        .eq('tenant_id', this.actualTenantId)
        .order('assessment_date', { ascending: true });

      if (subject) {
        query = query.eq('subject', subject);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching student SLP progress:', error);
        return { data: null, error };
      }

      // Group by subject and calculate progress
      const progressBySubject = {};
      data?.forEach(record => {
        if (!progressBySubject[record.subject]) {
          progressBySubject[record.subject] = {
            records: [],
            currentLevel: 0,
            progress: 0
          };
        }
        progressBySubject[record.subject].records.push(record);
        const level = parseInt(record.achievement_level) || 0;
        if (level > progressBySubject[record.subject].currentLevel) {
          progressBySubject[record.subject].currentLevel = level;
        }
      });

      // Calculate progress percentage for each subject
      Object.keys(progressBySubject).forEach(subject => {
        const subjectData = progressBySubject[subject];
        subjectData.progress = (subjectData.currentLevel / 5) * 100; // Assuming max level is 5
      });

      return {
        data: {
          progressBySubject,
          overallProgress: Object.values(progressBySubject).reduce((sum, s) => sum + s.progress, 0) / Object.keys(progressBySubject).length
        },
        error: null
      };

    } catch (error) {
      console.error('Error in getStudentSLPProgress:', error);
      return { data: null, error: error.message };
    }
  }

  /**
   * Get learning objectives for a subject
   * @param {string} subject - Subject name
   * @returns {Object} Learning objectives data
   */
  async getLearningObjectives(subject) {
    try {
      const { data, error } = await supabaseAdmin
        .from('slp_records')
        .select('learning_objective, achievement_level')
        .eq('subject', subject)
        .eq('tenant_id', this.actualTenantId)
        .not('learning_objective', 'is', null);

      if (error) {
        console.error('Error fetching learning objectives:', error);
        return { data: null, error };
      }

      // Group objectives by level
      const objectivesByLevel = {};
      data?.forEach(record => {
        const level = parseInt(record.achievement_level) || 0;
        if (!objectivesByLevel[level]) {
          objectivesByLevel[level] = [];
        }
        if (!objectivesByLevel[level].includes(record.learning_objective)) {
          objectivesByLevel[level].push(record.learning_objective);
        }
      });

      return {
        data: {
          objectivesByLevel,
          totalObjectives: data?.length || 0
        },
        error: null
      };

    } catch (error) {
      console.error('Error in getLearningObjectives:', error);
      return { data: null, error: error.message };
    }
  }
}

export default SLPService;